import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { AuthShell } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [stage, setStage] = useState("request");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const send = async () => {
    await api.post("/auth/forgot-password", { email });
    setCooldown(30);
  };

  const request = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await send();
      toast.success("If the email exists, a code has been sent");
      setStage("reset");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      await send();
      setCode("");
      toast.success("A new code has been sent. The previous code no longer works.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const reset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, code, new_password: password });
      toast.success("Password updated. Please log in.");
      navigate("/login");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Reset password" subtitle={stage === "request" ? "We'll email you a reset code" : "Enter the code and a new password"}>
      {stage === "request" ? (
        <form onSubmit={request} className="space-y-4" data-testid="forgot-request-form">
          <div>
            <Label htmlFor="fe">Email</Label>
            <Input id="fe" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" data-testid="forgot-email" />
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-submit">
            {loading ? "Sending…" : "Send reset code"}
          </Button>
        </form>
      ) : (
        <form onSubmit={reset} className="space-y-4" data-testid="forgot-reset-form">
          <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700" data-testid="forgot-sent-to">
            We sent a 6-digit code to <b>{email}</b>. It can take a minute to arrive, so check your spam folder too.
          </p>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="fc">Code</Label>
              <button type="button" onClick={resend} disabled={cooldown > 0} data-testid="forgot-resend"
                className="text-sm font-medium text-indigo-700 hover:underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline">
                {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
              </button>
            </div>
            <Input id="fc" required value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} className="mt-1.5" data-testid="forgot-code" />
          </div>
          <div>
            <Label htmlFor="fnp">New password</Label>
            <Input id="fnp" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5" placeholder="Min. 8 characters" data-testid="forgot-new-password" />
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-reset-submit">
            {loading ? "Updating…" : "Update password"}
          </Button>
          <button type="button" onClick={() => { setStage("request"); setCode(""); }} className="w-full text-center text-sm text-slate-600 hover:underline" data-testid="forgot-change-email">
            Use a different email
          </button>
        </form>
      )}
    </AuthShell>
  );
}
